// OAuth consent — the page an MCP client (Claude Desktop, claude.ai, Cursor,
// Claude Code, ChatGPT) opens so a signed-in member can let it act as them
// on this app. Sits behind RequireAuth (the instance login IS the identity);
// Approve posts the request to the backend (api/oauthServer.js), which
// validates client / redirect / PKCE, writes a single-use code and hands
// back the redirect. Nothing here can grant more than the member's seat.
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { getIdToken } from '../data/authPlane';
import { kitEnv } from '../data/kitEnv';

const API = kitEnv('UPLOAD_API', process.env.REACT_APP_UPLOAD_API || '');

const SCOPE_COPY = {
  read: 'Look up records and answer questions from the knowledge base',
  write: 'Create, update and delete records you are allowed to change',
};

export default function OAuthAuthorize() {
  const { user } = useAuth();
  const { search } = useLocation();
  const params = Object.fromEntries(new URLSearchParams(search).entries());
  const [client, setClient] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const requested = String(params.scope || '').split(/[\s,]+/).filter(Boolean);
  const [write, setWrite] = useState(requested.includes('write'));

  useEffect(() => {
    let live = true;
    if (!params.client_id) { setErr('Missing client_id — open this page from your AI client.'); return undefined; }
    fetch(`${API}/oauth/client?client_id=${encodeURIComponent(params.client_id)}`)
      .then(async (r) => { const j = await r.json().catch(() => ({})); if (!r.ok) throw new Error(j.error_description || j.error || `${r.status}`); return j; })
      .then((j) => live && setClient(j))
      .catch((ex) => live && setErr(`This client is not registered here (${ex.message}).`));
    return () => { live = false; };
  }, [params.client_id]);

  const deny = () => {
    try {
      const u = new URL(params.redirect_uri);
      u.searchParams.set('error', 'access_denied');
      if (params.state) u.searchParams.set('state', params.state);
      window.location.assign(u.toString());
    } catch { window.location.assign('/'); }
  };

  const approve = async () => {
    setBusy(true); setErr('');
    try {
      const token = await getIdToken();
      const res = await fetch(`${API}/oauth/authorize/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...params, scope: write ? 'read write' : 'read' }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error_description || j.error || `${res.status}`);
      window.location.assign(j.redirect);
    } catch (ex) { setErr(ex.message); setBusy(false); }
  };

  const appName = client?.app?.name || 'this app';
  return (
    <div className="cs-page" style={{ maxWidth: 520, margin: '0 auto' }}>
      <section className="cs-card" style={{ padding: 'var(--sp-6)' }}>
        <h1 style={{ marginBottom: 'var(--sp-1)' }}>Connect an AI client</h1>
        {client ? (
          <>
            <p className="cs-help">
              <strong>{client.client_name}</strong>{client.client_uri ? <> (<a href={client.client_uri} target="_blank" rel="noreferrer">{client.client_uri}</a>)</> : null} wants to use {appName} as <strong>{user?.email}</strong>.
              It will see exactly what your seat allows — no more.
            </p>
            <ul style={{ margin: 'var(--sp-4) 0', paddingLeft: 'var(--sp-5)', display: 'grid', gap: 'var(--sp-2)' }}>
              <li>{SCOPE_COPY.read}</li>
              <li>
                <label className="cs-switch">
                  <input type="checkbox" checked={write} onChange={(e) => setWrite(e.target.checked)} />
                  <span className="cs-switch-track" aria-hidden="true" />
                  <span>{SCOPE_COPY.write}{requested.includes('write') ? '' : ' (not requested — optional)'}</span>
                </label>
              </li>
            </ul>
            <p className="cs-help u-small">Revoke any time from Team › AI clients. Tokens expire and refresh on their own.</p>
            {err && <p className="cs-help" style={{ color: 'var(--danger)' }}>{err}</p>}
            <div className="u-cluster" style={{ marginTop: 'var(--sp-4)', justifyContent: 'flex-end' }}>
              <button className="cs-btn cs-btn-outline" type="button" onClick={deny} disabled={busy}>Cancel</button>
              <button className="cs-btn cs-btn-primary" type="button" onClick={approve} disabled={busy}>{busy ? 'Approving…' : 'Approve'}</button>
            </div>
          </>
        ) : (
          <p className="cs-help" style={err ? { color: 'var(--danger)' } : undefined}>{err || 'Loading…'}</p>
        )}
      </section>
    </div>
  );
}
